"use client";

import { useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { Topbar } from "@/components/topbar";
import { Upload, FileCheck2, AlertTriangle, XCircle } from "lucide-react";
import { formatMoneyCop } from "@/lib/utils";

type Preview = {
  filename: string;
  fileSize: number;
  rowsTotal: number;
  rowsValid: number;
  rowsDuplicate: number;
  rowsInvalid: number;
  sample: Array<{
    cedula: string;
    nombre: string;
    ciudad: string;
    monto: number;
    modelo: string;
    issue?: string;
  }>;
};

type ImportResult = {
  ok: boolean;
  imported: number;
  duplicate: number;
  invalid: number;
  fileId: string;
};

export default function ImportPage() {
  const router = useRouter();
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleFile(f: File) {
    setError(null);
    setResult(null);
    setFile(f);
    setLoading(true);
    const fd = new FormData();
    fd.append("file", f);
    fd.append("preview", "1");
    const res = await fetch("/api/import", { method: "POST", body: fd });
    setLoading(false);
    if (!res.ok) {
      setError(await res.text());
      return;
    }
    setPreview(await res.json());
  }

  async function handleConfirm() {
    if (!file) return;
    setLoading(true);
    setError(null);
    const fd = new FormData();
    fd.append("file", file);
    const res = await fetch("/api/import", { method: "POST", body: fd });
    setLoading(false);
    if (!res.ok) {
      setError(await res.text());
      return;
    }
    const json: ImportResult = await res.json();
    setResult(json);
  }

  return (
    <>
      <Topbar />
      <div className="p-8 max-w-[1280px]">
        <div className="font-display text-[11.5px] text-[#6B7280] uppercase tracking-wide font-semibold mb-2">
          Importar › Nuevo archivo
        </div>
        <h1 className="font-sans text-3xl font-black tracking-tight text-[#111] mb-1">
          Importar leads de <span className="text-[#CD0985]">Krediya</span>
        </h1>
        <p className="text-[#6B7280] text-sm font-display mb-6">
          Subí el Excel diario de aprobados-no-comprados · validación automática · cadencia automática
        </p>

        {/* DROP ZONE */}
        {!preview && !result && (
          <div
            className="bg-white border-[2px] border-dashed border-[#E5E7EB] rounded-[14px] p-16 text-center hover:border-[#CD0985] transition-colors cursor-pointer shadow-[0_1px_3px_rgba(0,0,0,0.04)]"
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              e.currentTarget.classList.add("border-[#CD0985]");
            }}
            onDragLeave={(e) => e.currentTarget.classList.remove("border-[#CD0985]")}
            onDrop={(e) => {
              e.preventDefault();
              e.currentTarget.classList.remove("border-[#CD0985]");
              const f = e.dataTransfer.files?.[0];
              if (f) handleFile(f);
            }}
          >
            <div className="w-16 h-16 rounded-full bg-[#FCE7F3] flex items-center justify-center mx-auto mb-4">
              <Upload size={28} className="text-[#CD0985]" strokeWidth={1.8} />
            </div>
            <h3 className="font-sans text-lg font-bold text-[#111] mb-2">
              {loading ? "Procesando..." : "Arrastrá el Excel o hacé click"}
            </h3>
            <p className="text-[#6B7280] text-sm font-display max-w-md mx-auto">
              Archivo .xlsx con columnas: cedula, nombre_completo, celular, correo, monto_aprobado_cop,
              fecha_aprob, modelo_celular, ciudad
            </p>
            <input
              ref={inputRef}
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleFile(f);
              }}
            />
          </div>
        )}

        {/* PREVIEW */}
        {preview && !result && (
          <div className="space-y-4">
            <Card>
              <div className="flex items-center gap-3.5 p-4 bg-[#D1FAE5] border border-[#86EFAC] rounded-[12px]">
                <div className="w-12 h-12 rounded-[10px] bg-[#A7F3D0] flex items-center justify-center text-2xl">
                  📄
                </div>
                <div className="flex-1">
                  <div className="font-bold text-[#065F46]">{preview.filename}</div>
                  <div className="font-display text-[11.5px] text-[#047857] mt-0.5 font-medium">
                    {(preview.fileSize / 1024).toFixed(0)} KB · {preview.rowsTotal} filas
                  </div>
                </div>
                <div className="font-display text-[12px] font-extrabold text-[#065F46] uppercase tracking-wide flex items-center gap-1.5">
                  <FileCheck2 size={14} strokeWidth={2.5} />
                  Formato válido
                </div>
              </div>
            </Card>

            <Card>
              <CardTitle>Validación del lote</CardTitle>
              <CardSub>Revisión automática antes de importar</CardSub>
              <div className="grid grid-cols-4 gap-3 mt-4">
                <Val accent="#10B981" num={preview.rowsTotal} label="Filas totales" />
                <Val accent="#10B981" num={preview.rowsValid} label="Válidos para cadencia" />
                <Val accent="#F59E0B" num={preview.rowsDuplicate} label="Duplicados (omitir)" />
                <Val accent="#EF4444" num={preview.rowsInvalid} label="Inválidos" />
              </div>

              <CardTitle className="mt-6">Preview · primeras 5 filas</CardTitle>
              <div className="mt-3 border border-[#E5E7EB] rounded-[11px] overflow-hidden">
                <div className="grid grid-cols-[110px_1.7fr_120px_120px_1fr_90px] text-[10.5px] font-display font-bold uppercase tracking-wider text-[#6B7280] bg-[#F9FAFB] border-b border-[#E5E7EB]">
                  <div className="px-3 py-2.5">Cédula</div>
                  <div className="px-3 py-2.5">Nombre</div>
                  <div className="px-3 py-2.5">Ciudad</div>
                  <div className="px-3 py-2.5">Monto COP</div>
                  <div className="px-3 py-2.5">Modelo</div>
                  <div className="px-3 py-2.5">Estado</div>
                </div>
                {preview.sample.map((row, i) => (
                  <div
                    key={i}
                    className="grid grid-cols-[110px_1.7fr_120px_120px_1fr_90px] text-[12px] border-t border-[#F3F4F6]"
                  >
                    <div className="px-3 py-2.5 text-[#111] font-mono">{row.cedula}</div>
                    <div className="px-3 py-2.5 text-[#111]">{row.nombre}</div>
                    <div className="px-3 py-2.5 text-[#111]">{row.ciudad}</div>
                    <div className="px-3 py-2.5 text-[#111] font-bold">
                      {formatMoneyCop(row.monto)}
                    </div>
                    <div className="px-3 py-2.5 text-[#111]">{row.modelo}</div>
                    <div className="px-3 py-2.5">
                      {row.issue === "duplicate" ? (
                        <span className="inline-block text-[10px] bg-[#FEE2E2] text-[#991B1B] px-2 py-0.5 rounded font-display font-bold uppercase tracking-wide">
                          Duplicado
                        </span>
                      ) : row.issue === "no-email" ? (
                        <span className="inline-block text-[10px] bg-[#FEF3C7] text-[#92400E] px-2 py-0.5 rounded font-display font-bold uppercase tracking-wide">
                          Sin email
                        </span>
                      ) : (
                        <span className="inline-block text-[10px] bg-[#D1FAE5] text-[#065F46] px-2 py-0.5 rounded font-display font-bold uppercase tracking-wide">
                          OK
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            <div className="bg-white border border-[#E5E7EB] rounded-[12px] p-5 flex items-center justify-between shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
              <div className="text-[13px]">
                Se importarán <b className="text-[#CD0985] text-[16px] font-extrabold">{preview.rowsValid}</b> leads
                {preview.rowsDuplicate > 0 && (
                  <span className="text-[#6B7280] font-display"> · {preview.rowsDuplicate} duplicados se omiten</span>
                )}
              </div>
              <div className="flex gap-2.5">
                <button
                  onClick={() => {
                    setPreview(null);
                    setFile(null);
                  }}
                  className="px-4 py-2.5 border-[1.5px] border-[#E5E7EB] bg-white text-[#111] rounded-[10px] font-display font-bold text-[12.5px] hover:border-[#CD0985] hover:text-[#CD0985]"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleConfirm}
                  disabled={loading}
                  className="px-5 py-2.5 bg-[#CD0985] hover:bg-[#9F0668] disabled:opacity-50 text-white rounded-[10px] font-display font-bold text-[13px] shadow-[0_4px_14px_rgba(205,9,133,0.35)]"
                >
                  {loading ? "Importando..." : "Importar leads →"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* RESULT */}
        {result && (
          <Card>
            <div className="text-center py-8">
              <div className="w-16 h-16 rounded-full bg-[#D1FAE5] flex items-center justify-center mx-auto mb-4">
                <FileCheck2 size={32} className="text-[#10B981]" strokeWidth={2} />
              </div>
              <h2 className="font-sans text-2xl font-black text-[#111] mb-2">¡Importación lista!</h2>
              <p className="text-[#6B7280] text-sm font-display mb-6">
                <b className="text-[#10B981]">{result.imported}</b> leads importados ·{" "}
                {result.duplicate > 0 && <>{result.duplicate} duplicados omitidos · </>}
                {result.invalid > 0 && <>{result.invalid} inválidos</>}
              </p>
              <button
                onClick={() => router.push("/leads")}
                className="px-6 py-3 bg-[#CD0985] hover:bg-[#9F0668] text-white rounded-[10px] font-display font-bold text-sm shadow-[0_4px_14px_rgba(205,9,133,0.35)]"
              >
                Ver leads →
              </button>
            </div>
          </Card>
        )}

        {error && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-[10px] text-sm text-red-800 flex items-start gap-2">
            <XCircle size={18} className="text-red-600 mt-0.5 flex-shrink-0" />
            <div>{error}</div>
          </div>
        )}
      </div>
    </>
  );
}

function Card({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-[14px] p-6 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
      {children}
    </div>
  );
}
function CardTitle({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return <h3 className={`font-sans text-[15px] font-bold text-[#111] mb-1 ${className}`}>{children}</h3>;
}
function CardSub({ children }: { children: React.ReactNode }) {
  return <p className="text-[11.5px] text-[#6B7280] font-display font-medium mb-4">{children}</p>;
}
function Val({ accent, num, label }: { accent: string; num: number; label: string }) {
  return (
    <div className="p-3.5 rounded-[11px] border" style={{ borderColor: `${accent}40`, background: `${accent}10` }}>
      <div className="font-sans text-2xl font-extrabold tracking-tight" style={{ color: accent }}>
        {num.toLocaleString("es-CO")}
      </div>
      <div className="font-display text-[10.5px] text-[#6B7280] font-bold tracking-wide uppercase mt-1">
        {label}
      </div>
    </div>
  );
}
